/**
 * The skill tool registry: three classified records (find_skill, get_skill,
 * manage_skill), deliberately separate from NATIVE_TOOL_REGISTRY.
 *
 * Skills are governed by their own scopes (`skills:read`, `skills:write`) and
 * their own kill switches (`skillLoading`, `skillWrites`), not by the native
 * per-tool AI policy. Keeping them in a second array is what stops a caller
 * from picking up skills by accident: `listToolsForGrant` stays native-only and
 * MCP concatenates the two lists explicitly.
 *
 * Every tool maps onto the unchanged /workspace/skills/* routes: the planners
 * below pick the route, and manage_skill's ten commands each keep their own
 * effect and role in SKILL_COMMAND_POLICY.
 */
import {
  BATCH_MAX,
  SKILL_PAGE_MAX_LIMIT,
  SKILL_QUERY_MAX_LENGTH,
  SkillAudienceSchema,
  SkillBundleInputSchema,
  SkillLocatorSchema,
  SkillReadLocatorSchema,
} from '@parcel/types';
import { z } from 'zod';
import { zodToJsonSchema } from 'zod-to-json-schema';
import { fnv1a64, stableStringify } from './native-registry.js';
import { type ParcelMcpGrantContext, planSatisfies } from './scopes.js';
import type { NativeToolEffect, ToolAnnotations, ToolDescriptor } from './types.js';

/** The runtime kill switch a skill tool answers to. */
export type SkillToolSwitch = 'skillLoading' | 'skillWrites';

export interface SkillToolDefinition {
  /** Stable identifier for storage and audit. Equals the descriptor name. */
  key: string;
  /** Content-derived; changes whenever the callable contract changes. */
  version: string;
  descriptor: ToolDescriptor;
  effect: NativeToolEffect;
  requiredScope: 'skills:read' | 'skills:write';
  requiredPlan: 'pro';
  requiredRole: 'member' | 'admin';
  requiredSwitch: SkillToolSwitch;
}

// ---------------------------------------------------------------------------
// Zod input schemas, one per tool
// ---------------------------------------------------------------------------

const IdempotencyKey = z
  .string()
  .min(1)
  .max(200)
  .describe('Caller-chosen key that makes a retry of this exact call safe. 1-200 characters.');

const SkillOverrideInput = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('custom'), skillId: z.string() }).strict(),
    z.object({ kind: z.literal('official'), officialSkillId: z.string() }).strict(),
  ])
  .describe(
    'The skill this one replaces for discovery. A Personal skill may override a Workspace or an official skill; a Workspace skill may override an official skill only.',
  );

// Mirrors the /workspace/skills/list facets, which come from the `skill` entity
// in @parcel/filters. Multi-value facets take one value or an array.
const multi = <T extends z.ZodTypeAny>(member: T) => z.union([member, z.array(member)]);

const LIST_FILTERS = {
  source: multi(z.enum(['custom', 'official']))
    .optional()
    .describe('Filter: where the skill came from, authored here or installed from the catalog.'),
  lifecycle: multi(z.enum(['draft', 'published', 'unpublished']))
    .optional()
    .describe('Filter: how far along the skill is: draft, live, or withdrawn from discovery.'),
  audience: multi(SkillAudienceSchema)
    .optional()
    .describe('Filter: who the skill is for, one member or the whole workspace.'),
  installed: z
    .enum(['installed', 'not_installed'])
    .optional()
    .describe('Filter: whether the skill was installed from the official catalog.'),
  updated_after: z
    .string()
    .date()
    .optional()
    .describe('Filter: last changed on or after this date (YYYY-MM-DD).'),
  updated_before: z
    .string()
    .date()
    .optional()
    .describe('Filter: last changed before this date (YYYY-MM-DD, exclusive).'),
  sort: z
    .enum(['name', 'updated_at'])
    .optional()
    .describe('List sort key. Defaults to updated_at.'),
  direction: z.enum(['asc', 'desc']).optional().describe('List sort direction. Defaults to desc.'),
} as const;

/** The list facets: any of them makes find_skill a filtered list instead of a ranked search. */
export const SKILL_LIST_FILTER_KEYS = Object.keys(
  LIST_FILTERS,
) as readonly (keyof typeof LIST_FILTERS)[];

const FindSkillToolInput = z
  .object({
    query: z
      .string()
      .max(SKILL_QUERY_MAX_LENGTH)
      .optional()
      .describe(
        'Free text matched against skill names and descriptions. Without filters it is a ranked search; with filters it narrows the list.',
      ),
    scope: z
      .enum(['active', 'explore'])
      .optional()
      .describe(
        'Search only. `active` searches custom and installed skills; `explore` searches the official catalog only, including skills already installed, and reports the `installed` flag on each, but never returns custom skills.',
      ),
    ...LIST_FILTERS,
    limit: z
      .number()
      .int()
      .min(1)
      .optional()
      .describe(`Maximum summaries: up to 20 for a search, ${SKILL_PAGE_MAX_LIMIT} for a list.`),
  })
  .strict();

const READ_LOCATOR_HINT =
  'The skill to read. Pass { source: "name", name } with the name the catalog shows; an id locator also works.';

const GetSkillToolInput = z
  .object({
    locator: SkillReadLocatorSchema.describe(READ_LOCATOR_HINT),
    path: z
      .string()
      .optional()
      .describe(
        'Path of one file inside the skill bundle; returns that file instead of the skill.',
      ),
  })
  .strict();

const CreateSkillInput = z
  .object({
    audience: SkillAudienceSchema.describe('Personal keeps it to you; Workspace shares it.'),
    bundle: SkillBundleInputSchema.describe('Portable bundle. Prose and inert data only.'),
    override: SkillOverrideInput.optional(),
    authoringConversationId: z
      .string()
      .optional()
      .describe('Spec conversation this skill was authored in.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const UpdateSkillInput = z
  .object({
    skillId: z.string().describe('The skill to edit.'),
    expectedDraftVersion: z
      .number()
      .int()
      .describe('Draft version you last read. A mismatch rejects the write.'),
    bundle: SkillBundleInputSchema.optional().describe('Replacement bundle for the draft.'),
    restoreRevisionId: z
      .string()
      .optional()
      .describe('Restore the draft from this published revision instead.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const PublishSkillInput = z
  .object({
    skillId: z.string().describe('The skill to publish.'),
    expectedDraftVersion: z
      .number()
      .int()
      .describe('Draft version you last read. A mismatch rejects the write.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const VersionedSkillInput = z
  .object({
    skillId: z.string().describe('The skill to act on.'),
    expectedVersion: z
      .number()
      .int()
      .describe('Version you last read. A mismatch rejects the write.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const TransferSkillInput = z
  .object({
    skillId: z.string().describe('The Workspace skill to hand over.'),
    expectedVersion: z
      .number()
      .int()
      .describe('Version you last read. A mismatch rejects the write.'),
    newMaintainerId: z.string().describe('Member who becomes the maintainer.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const MoveSkillInput = z
  .object({
    skillId: z.string().describe('The custom skill to move.'),
    expectedVersion: z
      .number()
      .int()
      .describe('Version you last read. A mismatch rejects the write.'),
    audience: z
      .enum(['personal', 'workspace'])
      .describe('Where the skill goes. The mover becomes its owner or maintainer.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const ForkSkillInput = z
  .object({
    source: SkillLocatorSchema.describe('The readable skill to copy.'),
    audience: SkillAudienceSchema.describe('Personal keeps the fork to you; Workspace shares it.'),
    override: SkillOverrideInput.optional(),
    name: z
      .string()
      .min(1)
      .max(64)
      .optional()
      .describe("Name for the fork, 1-64 characters. Omitted keeps the source's name."),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const InstallSkillInput = z
  .object({
    officialSkillId: z.string().describe('The official skill to install.'),
    releaseId: z
      .string()
      .optional()
      .describe(
        'Release to install or re-pin to. Omitted keeps an existing installation as is, or installs latest.',
      ),
    audience: SkillAudienceSchema.describe('Personal installs it for you; Workspace for everyone.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

const UninstallSkillInput = z
  .object({
    installationId: z.string().describe('The installation to deactivate.'),
    expectedVersion: z
      .number()
      .int()
      .describe('Version you last read. A mismatch rejects the write.'),
    idempotencyKey: IdempotencyKey,
  })
  .strict();

/** manage_skill's commands, in advertised order; each is one /workspace/skills/<route> write. */
export const SKILL_COMMANDS = [
  'create',
  'update',
  'publish',
  'unpublish',
  'delete',
  'transfer',
  'move',
  'fork',
  'install',
  'uninstall',
] as const;

export type SkillCommand = (typeof SKILL_COMMANDS)[number];

export interface SkillCommandPolicy {
  effect: NativeToolEffect;
  role: 'member' | 'admin';
  /** The route segment under /workspace/skills/, with `/batch` for `inputs`. */
  route: string;
}

const MEMBER_WRITE = { effect: 'reversible_write', role: 'member' } as const;

export const SKILL_COMMAND_POLICY: Readonly<Record<SkillCommand, SkillCommandPolicy>> = {
  create: { ...MEMBER_WRITE, route: 'create' },
  update: { ...MEMBER_WRITE, route: 'update' },
  publish: { ...MEMBER_WRITE, route: 'publish' },
  unpublish: { ...MEMBER_WRITE, route: 'unpublish' },
  delete: { effect: 'destructive', role: 'member', route: 'delete' },
  transfer: { effect: 'reversible_write', role: 'admin', route: 'transfer' },
  move: { ...MEMBER_WRITE, route: 'move' },
  fork: { ...MEMBER_WRITE, route: 'fork' },
  install: { ...MEMBER_WRITE, route: 'install' },
  uninstall: { ...MEMBER_WRITE, route: 'uninstall' },
};

/** Each command's fields: the route body, plus the idempotencyKey a single call sends as a header. */
export const SKILL_COMMAND_INPUTS = {
  create: CreateSkillInput,
  update: UpdateSkillInput,
  publish: PublishSkillInput,
  unpublish: VersionedSkillInput,
  delete: VersionedSkillInput,
  transfer: TransferSkillInput,
  move: MoveSkillInput,
  fork: ForkSkillInput,
  install: InstallSkillInput,
  uninstall: UninstallSkillInput,
} as const satisfies Record<SkillCommand, z.AnyZodObject>;

// A Set, not `in`: an object lookup would accept "__proto__" and "constructor".
const COMMAND_SET: ReadonlySet<string> = new Set(SKILL_COMMANDS);

export function isSkillCommand(value: unknown): value is SkillCommand {
  return typeof value === 'string' && COMMAND_SET.has(value);
}

const withCommand = <C extends SkillCommand>(command: C) =>
  SKILL_COMMAND_INPUTS[command].extend({ command: z.literal(command) });

/** One manage_skill call or batch item: a command and that command's fields. */
export const ManageSkillCommandInput = z.discriminatedUnion('command', [
  withCommand('create'),
  withCommand('update'),
  withCommand('publish'),
  withCommand('unpublish'),
  withCommand('delete'),
  withCommand('transfer'),
  withCommand('move'),
  withCommand('fork'),
  withCommand('install'),
  withCommand('uninstall'),
]);

export interface SkillToolInputSchemas {
  /** The single-call form. */
  single: z.ZodTypeAny;
  /** One element of `inputs`. Same shape as `single`; absent on the reads. */
  item?: z.ZodTypeAny;
}

export const SKILL_TOOL_INPUT_SCHEMAS = {
  find_skill: { single: FindSkillToolInput, item: undefined },
  get_skill: { single: GetSkillToolInput, item: undefined },
  manage_skill: { single: ManageSkillCommandInput, item: ManageSkillCommandInput },
} as const satisfies Record<string, SkillToolInputSchemas>;

export const SKILL_TOOL_NAMES = ['find_skill', 'get_skill', 'manage_skill'] as const;

export type SkillToolName = (typeof SKILL_TOOL_NAMES)[number];

// ---------------------------------------------------------------------------
// JSON Schema derivation
// ---------------------------------------------------------------------------

interface DerivedObjectSchema {
  type: string;
  properties: Record<string, unknown>;
  required?: string[];
  additionalProperties?: boolean;
}

/** JSON Schema for one Zod object, with the draft URL stripped off. */
function toObjectSchema(schema: z.ZodTypeAny): DerivedObjectSchema {
  const { $schema, ...rest } = zodToJsonSchema(schema, { $refStrategy: 'none' }) as Record<
    string,
    unknown
  > & { $schema?: string };
  return rest as unknown as DerivedObjectSchema;
}

function readInputSchema(schema: z.ZodTypeAny): ToolDescriptor['inputSchema'] {
  const derived = toObjectSchema(schema);
  return {
    type: 'object',
    properties: derived.properties,
    ...(derived.required ? { required: derived.required } : {}),
    additionalProperties: false,
  };
}

// A field two listed commands share gets one description that fits both; a narrowed schema keeps each command's own.
const SHARED_FIELD_DESCRIPTIONS: Readonly<Record<string, string>> = {
  skillId: 'The custom skill to act on (update, publish, unpublish, delete, transfer, move).',
  audience:
    'Personal or Workspace: where a created, forked or installed skill lives, or where a moved one goes.',
  bundle:
    'Portable bundle, prose and inert data only. Required to create; on update it replaces the draft.',
  override:
    'The skill this one replaces for discovery (create, fork). A Personal skill may override a Workspace or an official skill; a Workspace skill may override an official skill only.',
  expectedVersion:
    'Version you last read (unpublish, delete, transfer, move, uninstall). A mismatch rejects the write.',
  expectedDraftVersion:
    'Draft version you last read (update, publish). A mismatch rejects the write.',
};

/** Every listed command's fields in one flat object, plus each command's required fields. */
export function skillCommandFields(commands: readonly SkillCommand[]): {
  properties: Record<string, unknown>;
  branches: { properties: { command: { const: SkillCommand } }; required: string[] }[];
} {
  const properties: Record<string, unknown> = {
    command: {
      type: 'string',
      enum: [...commands],
      description: 'What to do. Each command takes its own fields.',
    },
  };
  const branches: { properties: { command: { const: SkillCommand } }; required: string[] }[] = [];
  const derivedAll = commands.map((command) => toObjectSchema(SKILL_COMMAND_INPUTS[command]));
  const uses = (key: string) => derivedAll.filter((derived) => key in derived.properties).length;
  for (const [index, command] of commands.entries()) {
    const derived = derivedAll[index] as DerivedObjectSchema;
    for (const [key, value] of Object.entries(derived.properties)) {
      if (key in properties) continue;
      const description = uses(key) > 1 ? SHARED_FIELD_DESCRIPTIONS[key] : undefined;
      properties[key] = description ? { ...(value as object), description } : value;
    }
    branches.push({
      properties: { command: { const: command } },
      required: derived.required ?? [],
    });
  }
  return { properties, branches };
}

/**
 * The flat command schema (the manage_knowledge shape): one command's fields, or
 * `inputs` when batch is on. oneOf means "exactly one branch", so a body carrying
 * both forms fails validation rather than being read as one or the other.
 */
export function manageSkillInputSchema(
  commands: readonly SkillCommand[],
  opts: { batch: boolean },
): ToolDescriptor['inputSchema'] {
  const { properties, branches } = skillCommandFields(commands);
  const single = { required: ['command'], oneOf: branches };
  if (!opts.batch) return { type: 'object', properties, ...single, additionalProperties: false };
  return {
    type: 'object',
    properties: {
      ...properties,
      inputs: {
        type: 'array',
        items: {
          type: 'object',
          properties,
          required: ['command'],
          oneOf: branches,
          additionalProperties: false,
        },
        minItems: 1,
        maxItems: BATCH_MAX,
        description:
          'Batch: 1-100 items of one command, each with its own fields and idempotencyKey. Use INSTEAD of the top level.',
      },
    },
    oneOf: [{ required: ['inputs'] }, single],
    additionalProperties: false,
  };
}

// ---------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------

/**
 * The immutable contract version. Covers the name, the input schema, and the
 * classification that governs a call. Description and annotations are excluded
 * so rewording copy or retuning a client hint does not bump the version.
 */
export function skillToolContractVersion(
  tool: Pick<
    SkillToolDefinition,
    'descriptor' | 'effect' | 'requiredScope' | 'requiredRole' | 'requiredSwitch'
  >,
): string {
  const content = stableStringify({
    name: tool.descriptor.name,
    inputSchema: tool.descriptor.inputSchema,
    effect: tool.effect,
    requiredScope: tool.requiredScope,
    requiredRole: tool.requiredRole,
    requiredSwitch: tool.requiredSwitch,
  });
  return `v1.${fnv1a64(content).slice(0, 12)}`;
}

// ---------------------------------------------------------------------------
// The three definitions
// ---------------------------------------------------------------------------

const READ_ONLY: ToolAnnotations = { readOnlyHint: true };
// One command (delete) is destructive, so the tool says so; every command is keyed-idempotent.
const KEYED_DESTRUCTIVE: ToolAnnotations = {
  readOnlyHint: false,
  destructiveHint: true,
  idempotentHint: true,
};

type SkillToolSpec = Omit<SkillToolDefinition, 'key' | 'version'>;

function read(name: 'find_skill' | 'get_skill', description: string): SkillToolSpec {
  return {
    descriptor: {
      name,
      description,
      inputSchema: readInputSchema(SKILL_TOOL_INPUT_SCHEMAS[name].single),
      annotations: READ_ONLY,
    },
    effect: 'read',
    requiredScope: 'skills:read',
    requiredPlan: 'pro',
    requiredRole: 'member',
    requiredSwitch: 'skillLoading',
  };
}

const MANAGE_SKILL_DESCRIPTION =
  'Write skills: create, update, publish, unpublish, delete, transfer, move, fork, install or uninstall, chosen by command. New skills start as drafts; publish makes one discoverable; delete stops discovery for good; transfer is for owners and admins. Each single call needs an idempotencyKey. Prefer one batch call (inputs, up to 100, one command per batch).';

// L1 discovery is metadata only. The wording says so on purpose: a model that
// believes find_skill returns instructions will try to skip get_skill.
const SKILL_TOOL_SPECS: readonly SkillToolSpec[] = [
  read(
    'find_skill',
    'Find skills in this workspace as content-free summaries: name, description, audience, lifecycle, and locator. With a query (or scope) and no filters: a ranked search that applies override precedence; scope `explore` searches the official catalog. With filters (source, lifecycle, audience, installed, updated dates, sort, direction): the list, which shows an overridden skill beside its override, narrowed by query. With neither: the list. Never returns skill instructions or file contents; call get_skill to read one.',
  ),
  read(
    'get_skill',
    'Read one skill: its instructions, metadata, and the list of files it carries. Pass { source: "name", name } with the name the catalog shows. Add path to read the contents of one file inside it instead.',
  ),
  {
    descriptor: {
      name: 'manage_skill',
      description: MANAGE_SKILL_DESCRIPTION,
      inputSchema: manageSkillInputSchema(SKILL_COMMANDS, { batch: true }),
      annotations: KEYED_DESTRUCTIVE,
    },
    // The tool's floor; adminSkillCommands tightens the role per planned call (transfer); delete is described, not refused.
    effect: 'reversible_write',
    requiredScope: 'skills:write',
    requiredPlan: 'pro',
    requiredRole: 'member',
    requiredSwitch: 'skillWrites',
  },
];

export const SKILL_TOOL_REGISTRY: readonly SkillToolDefinition[] = SKILL_TOOL_SPECS.map((spec) => ({
  ...spec,
  key: spec.descriptor.name,
  version: skillToolContractVersion(spec),
}));

// A Map, not a bare object: an object lookup would resolve "__proto__" and
// "constructor" to inherited values and hand back a truthy non-tool.
const REGISTRY_BY_NAME = new Map(SKILL_TOOL_REGISTRY.map((tool) => [tool.descriptor.name, tool]));

export function getSkillTool(name: string): SkillToolDefinition | null {
  return REGISTRY_BY_NAME.get(name) ?? null;
}

// ---------------------------------------------------------------------------
// Grant authorization
// ---------------------------------------------------------------------------

const ROLE_RANK: Readonly<Record<'member' | 'admin' | 'owner', number>> = {
  member: 0,
  admin: 1,
  owner: 2,
};

function switchIsOn(grant: ParcelMcpGrantContext, required: SkillToolSwitch): boolean {
  const capabilities = grant.skillCapabilities;
  if (!capabilities) return false;
  return required === 'skillLoading' ? capabilities.loading : capabilities.writes;
}

/**
 * Tool-level eligibility, which is what discovery answers. Deliberately not the
 * native rule: the native per-tool AI policy governs native tools only, and a
 * destructive effect is descriptive here rather than a refusal.
 */
function canGrantListSkillTool(grant: ParcelMcpGrantContext, tool: SkillToolDefinition): boolean {
  if (!grant.membershipActive) return false;
  if (!planSatisfies(grant.plan, tool.requiredPlan)) return false;
  if (ROLE_RANK[grant.role] < ROLE_RANK[tool.requiredRole]) return false;
  if (!grant.scopes.has(tool.requiredScope)) return false;
  return switchIsOn(grant, tool.requiredSwitch);
}

export function listSkillToolsForGrant(grant: ParcelMcpGrantContext): readonly ToolDescriptor[] {
  return SKILL_TOOL_REGISTRY.filter((tool) => canGrantListSkillTool(grant, tool)).map(
    (tool) => tool.descriptor,
  );
}

/**
 * A write body carrying both the single fields and `inputs` is ambiguous, so it
 * is refused here as well as by the schema. "Neither" is left to validation:
 * this is an authorization gate, not a validator.
 */
function mixesSingleAndBatch(
  tool: SkillToolDefinition,
  args: Readonly<Record<string, unknown>>,
): boolean {
  return tool.requiredScope === 'skills:write' && mixesSkillCallForms(args);
}

/** Single fields beside `inputs`; `workspace_id` is the dispatcher's pin, not half of the XOR. */
export function mixesSkillCallForms(args: Readonly<Record<string, unknown>>): boolean {
  const keys = Object.keys(presentSkillFields(args)).filter((key) => key !== 'workspace_id');
  return keys.includes('inputs') && keys.some((key) => key !== 'inputs');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function canGrantCallSkillToolByName(
  grant: ParcelMcpGrantContext,
  name: string,
  args: Readonly<Record<string, unknown>> = {},
): boolean {
  const tool = getSkillTool(name);
  if (!tool) return false;
  if (!canGrantListSkillTool(grant, tool)) return false;
  if (mixesSingleAndBatch(tool, args)) return false;
  if (tool.requiredScope !== 'skills:write') return true;
  // The role is checked on the call the dispatcher will make, so the two cannot disagree. Destructive is
  // described, not refused (as delete_skill was); a refused plan never reaches a route.
  return adminSkillCommands(name, args).length === 0 || ROLE_RANK[grant.role] >= ROLE_RANK.admin;
}

/** The admin-only command (transfer) the planned call would run, if any. */
export function adminSkillCommands(
  name: string,
  args: Readonly<Record<string, unknown>>,
): SkillCommand[] {
  const plan = planSkillCall(name, args);
  return plan?.kind === 'call' &&
    plan.command &&
    SKILL_COMMAND_POLICY[plan.command].role === 'admin'
    ? [plan.command]
    : [];
}

// ---------------------------------------------------------------------------
// Call planning, shared by MCP and Spec
// ---------------------------------------------------------------------------

export type SkillCallPlan =
  | {
      kind: 'call';
      path: string;
      body: Record<string, unknown>;
      idempotencyKey?: string;
      /** manage_skill's command, which the role check reads. */
      command?: SkillCommand;
    }
  | { kind: 'refusal'; message: string };

const SKILLS = '/workspace/skills';

/** The fields a call carries: undefined, null and '' all mean absent, since some clients fill every optional field. */
export function presentSkillFields(
  args: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(args).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

const LIST_FILTER_SET: ReadonlySet<string> = new Set(SKILL_LIST_FILTER_KEYS);
// The service's cap on a ranked search (FIND_HARD_CAP in the skill service).
const FIND_RESULTS_MAX = 20;

/** find_skill: a ranked search with a query or scope and no filters, the list otherwise. */
export function planFindSkill(args: Readonly<Record<string, unknown>>): SkillCallPlan {
  // `q` is the list's own text field.
  const { q: listText, ...rest } = presentSkillFields(args);
  if (listText !== undefined && rest.query !== undefined) {
    return { kind: 'refusal', message: 'find_skill: send query or q, not both.' };
  }
  const { query, scope, limit, ...facets } = rest;
  // `q` alone still asks for the list, as list_skills did, so it counts as a filter.
  const filters = listText === undefined ? facets : { ...facets, q: listText };
  // A stray key is named here, not read as a filter that turns the search into a list.
  const stray = Object.keys(facets).filter((key) => !LIST_FILTER_SET.has(key));
  if (stray.length > 0) {
    return { kind: 'refusal', message: `find_skill does not take ${stray.join(', ')}.` };
  }
  const filtered = Object.keys(filters).length > 0;
  // Each route has its cap (a search returns 20, a list page 200); a larger limit is clamped, not refused.
  // A limit that is no number goes through for the route to refuse, never silently dropped.
  const capped = (max: number) => ({
    limit: typeof limit === 'number' ? Math.min(limit, max) : limit,
  });
  const limited = limit === undefined ? {} : capped(SKILL_PAGE_MAX_LIMIT);
  if (filtered) {
    if (scope !== undefined) {
      return {
        kind: 'refusal',
        message: 'find_skill: scope applies to a search, not to filters; drop one of them.',
      };
    }
    // A query of the wrong type goes through as q for the route to refuse, never silently dropped.
    const q = query === undefined ? {} : { q: query };
    return { kind: 'call', path: `${SKILLS}/list`, body: { ...filters, ...q, ...limited } };
  }
  // A query of '' alone is still a ranked search over everything, as find_skill's required query was.
  if (query !== undefined || scope !== undefined || typeof args.query === 'string') {
    const scoped = scope === undefined ? {} : { scope };
    const searchLimit = limit === undefined ? {} : capped(FIND_RESULTS_MAX);
    return {
      kind: 'call',
      path: `${SKILLS}/find`,
      body: { query: query ?? '', ...searchLimit, ...scoped },
    };
  }
  return { kind: 'call', path: `${SKILLS}/list`, body: limited };
}

/** get_skill: one file with a path, the skill otherwise; a path left '' or null is no path. */
export function planGetSkill(args: Readonly<Record<string, unknown>>): SkillCallPlan {
  const { path, ...rest } = presentSkillFields(args);
  return path === undefined
    ? { kind: 'call', path: `${SKILLS}/get`, body: rest }
    : { kind: 'call', path: `${SKILLS}/get-file`, body: { ...rest, path } };
}

/** A single call's fields the command does not take; a batch route answers each item itself. */
export function foreignSkillFields(
  command: SkillCommand,
  fields: Readonly<Record<string, unknown>>,
): string[] {
  const shape = (SKILL_COMMAND_INPUTS[command] as { shape: Record<string, unknown> }).shape;
  return Object.keys(fields).filter((key) => key !== 'command' && !Object.hasOwn(shape, key));
}

const COMMAND_LIST = SKILL_COMMANDS.join(', ');

/** manage_skill: one command's route, or its `/batch` sibling for `inputs` of one command. */
export function planManageSkill(args: Readonly<Record<string, unknown>>): SkillCallPlan {
  const fields = presentSkillFields(args);
  if (fields.inputs !== undefined) {
    if (Object.keys(fields).some((key) => key !== 'inputs')) {
      return {
        kind: 'refusal',
        message: 'Send either the single fields or "inputs" to manage_skill, never both.',
      };
    }
    const items = Array.isArray(fields.inputs) ? fields.inputs : [];
    // The batch's command is the one its items name; an item that names none, or is no object, is the route's to answer.
    const named = (item: unknown) =>
      isRecord(item) && presentSkillFields(item).command !== undefined;
    const commands = new Set(
      items.filter(named).map((item) => (item as Record<string, unknown>).command),
    );
    const [command] = commands;
    if (items.length === 0 || commands.size !== 1 || !isSkillCommand(command)) {
      return {
        kind: 'refusal',
        message: `manage_skill: batch one command at a time; the items in inputs name one command (${COMMAND_LIST}).`,
      };
    }
    // The batch routes are per command, so the command travels in the path, not in each item.
    // An item that named no command keeps a null one, which the strict route refuses for that item alone, so the
    // batch's command (delete, say) is never applied to an item that did not ask for it.
    const bodies = items.map((item) => {
      if (!isRecord(item)) return item;
      const { command: _command, ...body } = presentSkillFields(item);
      return named(item) ? body : { ...body, command: null };
    });
    return {
      kind: 'call',
      path: `${SKILLS}/${SKILL_COMMAND_POLICY[command].route}/batch`,
      body: { inputs: bodies },
      command,
    };
  }
  const { command, idempotencyKey, ...body } = fields;
  if (!isSkillCommand(command)) {
    return { kind: 'refusal', message: `manage_skill needs a command: ${COMMAND_LIST}.` };
  }
  const foreign = foreignSkillFields(command, body);
  if (foreign.length > 0) {
    return {
      kind: 'refusal',
      message: `manage_skill ${command} does not take ${foreign.join(', ')}.`,
    };
  }
  if (typeof idempotencyKey !== 'string' || idempotencyKey.length === 0) {
    return {
      kind: 'refusal',
      message: `manage_skill ${command} requires an "idempotencyKey" so retrying it is safe.`,
    };
  }
  // The key travels as the Idempotency-Key header; the strict single-record body refuses it.
  return {
    kind: 'call',
    path: `${SKILLS}/${SKILL_COMMAND_POLICY[command].route}`,
    body,
    idempotencyKey,
    command,
  };
}

const PLANNERS: ReadonlyMap<string, (args: Readonly<Record<string, unknown>>) => SkillCallPlan> =
  new Map([
    ['find_skill', planFindSkill],
    ['get_skill', planGetSkill],
    ['manage_skill', planManageSkill],
  ]);

/**
 * The one plan both the MCP gate and its dispatcher read: the grant pins the workspace, so a model-supplied
 * workspace_id is dropped first. Null for a name that is no skill tool.
 */
export function planSkillCall(
  name: string,
  args: Readonly<Record<string, unknown>>,
): SkillCallPlan | null {
  const planner = PLANNERS.get(name);
  if (!planner) return null;
  const { workspace_id: _pinned, ...rest } = args;
  return planner(rest);
}
